import React from 'react';
import OptionPicker from './OptionPicker';
import RichBody from './RichBody';
import ReelPicker from './ReelPicker';
import { TEXT_MAX } from '../data/constants';

/**
 * One field, rendered by `kind` plus whether the server sent options:
 *   list + options → capped picker
 *   list, no options → one box per pick the cap allows
 *   text → input, or textarea when it has no cap
 */
// A link field is a single line; anything else the client writes at length is
// a textarea unless it shares its card with another input.
// TODO(EG-1599): replace with an explicit variant on the field when the
// payload carries one.
export const isLinkField = field => /(^|_)(link|url)$/.test(field.key || '');
const Field = ({
  section,
  field,
  value,
  group,
  locked,
  isPhone,
  bare,
  paired,
  hideLabel,
  onToggle,
  onText,
  onValue
}) => {
  // Generic, not an interviews special case: a field shows only when the
  // named field in the same section holds that value.
  const reveal = field.reveal_when;
  if (reveal && String((group || {})[reveal.field] ?? '') !== String(reveal.value)) {
    return null;
  }

  // Read-only copy the server supplies. Never sent in a PATCH. The body
  // carries its own structure — cards, notes, an expandable question — so it
  // is parsed rather than printed.
  if (field.kind === 'display') {
    const body = React.createElement(RichBody, {
      body: field.body
    });

    // Inside a card of its own, or bare when a card already wraps it.
    if (bare) return body;
    return React.createElement("div", {
      className: "cp-ivcard cp-field-block"
    }, field.label && React.createElement("p", {
      className: "cp-qlabel"
    }, field.label), body);
  }

  // One answer out of a few. Pressing the active pill again clears it.
  if (field.kind === 'choice') {
    return React.createElement("div", {
      className: "cp-ivcard cp-field-block"
    }, field.label && React.createElement("p", {
      className: "cp-qlabel"
    }, field.label), section.question && React.createElement("p", {
      className: "cp-ivq"
    }, section.question), React.createElement("div", {
      className: "cp-ivpills"
    }, (field.options || []).map(option => React.createElement("button", {
      key: option.id,
      type: "button",
      className: `cp-ivpill${String(value ?? '') === String(option.id) ? ' is-on' : ''}`,
      "aria-pressed": String(value ?? '') === String(option.id),
      disabled: locked,
      onClick: () => onValue(section.key, field.key, option.id)
    }, option.name))));
  }
  if (field.kind === 'list' && (field.options || []).length) {
    // The option is the playlist to read from, not a value to store: the
    // client picks videos out of it and the answer holds video ids.
    const Picker = field.options_are_sources ? ReelPicker : OptionPicker;
    return React.createElement("div", {
      className: "cp-field-block"
    }, React.createElement(Picker, {
      field: field,
      picked: value || [],
      locked: locked,
      isPhone: isPhone,
      onToggle: id => onToggle(section.key, field.key, id, field.max_picks)
    }));
  }
  if (field.kind === 'list') {
    // Free-form: the client's own words, checked against nothing. The cap
    // is the number of boxes, so how many are wanted is on screen from the
    // start rather than discovered by filling one and being refused the
    // next. Without a cap there is one box and nothing to count.
    const picked = value || [];
    const boxes = field.max_picks || 1;
    // Trailing blanks are nobody's answer, so they never reach the store.
    const writeAt = (i, text) => {
      const next = [];
      for (let n = 0; n < boxes; n += 1) {
        next.push(n === i ? text : picked[n] || '');
      }
      while (next.length && !String(next[next.length - 1]).trim()) next.pop();
      onText(section.key, field.key, next);
    };
    return React.createElement("div", {
      className: "cp-field-block cp-freelist"
    }, field.label && React.createElement("p", {
      className: "cp-fieldlabel"
    }, field.label), React.createElement("div", {
      className: "cp-listlinks"
    }, Array.from({
      length: boxes
    }, (ignored, i) => React.createElement("input", {
      key: i,
      type: "url",
      inputMode: "url",
      className: "cp-listlink",
      "aria-label": `${field.label || 'Link'} ${i + 1}`,
      placeholder: "Paste your link",
      value: picked[i] || '',
      disabled: locked,
      onChange: e => writeAt(i, e.target.value)
    }))));
  }
  if (isLinkField(field)) {
    return React.createElement("div", {
      className: "cp-linkcard"
    }, field.label && !hideLabel && React.createElement("p", {
      className: "cp-linkcard-copy"
    }, field.label), React.createElement("input", {
      type: "url",
      inputMode: "url",
      "aria-label": field.label,
      placeholder: "https://",
      value: value || '',
      disabled: locked,
      onChange: e => onText(section.key, field.key, e.target.value)
    }));
  }
  const long = !paired;
  // What the client has written against the cap the textarea enforces.
  const used = String(value || '').length;
  return React.createElement("div", {
    className: "cp-field-block"
  }, field.label && !hideLabel && React.createElement("p", {
    className: "cp-fieldlabel"
  }, field.label), long ? React.createElement(React.Fragment, null, React.createElement("textarea", {
    className: "cp-textarea",
    rows: isPhone ? 6 : 5,
    maxLength: TEXT_MAX,
    "aria-label": field.label || field.placeholder,
    placeholder: field.placeholder || '',
    value: value || '',
    disabled: locked,
    onChange: e => onText(section.key, field.key, e.target.value)
  }), React.createElement("p", {
    className: `cp-count${used >= TEXT_MAX ? ' is-full' : ''}`,
    "aria-live": "polite"
  }, used, " / ", TEXT_MAX, " characters")) : React.createElement("input", {
    type: "text",
    className: "cp-iginput",
    "aria-label": field.label || field.placeholder,
    placeholder: field.placeholder || '',
    value: value || '',
    disabled: locked,
    onChange: e => onText(section.key, field.key, e.target.value)
  }));
};
export default Field;
